import React, { useContext, useEffect, useState } from 'react';
import { Form, Image } from 'react-bootstrap';
import { IoIosCamera } from 'react-icons/io';
import { mixed, object, Schema } from 'yup';

import placeholder from 'src/assets/images/placeholder-img.svg';
import { AppViewContext } from 'src/containers/Core/AppView';

type DispatchToProps = {};

type FormValues = {
  upload: File;
};

export type ImageCallback = (data: FormData) => void;

type ImgUploadProps = {
  img?: string;
  onSubmitCallback: ImageCallback;
  objkey: string;
  customPlaceholder?: React.ReactNode;
  className?: string;
} & DispatchToProps;

const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/png'];
const FILE_SIZE = 2000000;

const schema: Schema<FormValues> = object({
  upload: mixed()
    .required()
    .test('fileSize', 'File Size is too large', (value: File) => {
      return value.size <= FILE_SIZE;
    })
    .test('fileType', 'Unsupported File Format', (value: File) => SUPPORTED_FORMATS.includes(value.type)),
});

const UploadImg: React.FunctionComponent<ImgUploadProps> = ({
  img,
  onSubmitCallback,
  objkey,
  customPlaceholder,
  className,
}) => {
  const [upload, setUpload] = useState<File>(null);
  const [preview, setPreview] = useState(null);
  const [isInvalid, setIsInvalid] = useState(false);
  const { isPhoneView } = useContext(AppViewContext);

  useEffect(() => {
    if (upload) {
      schema
        .validate({ upload })
        .then(v => {
          const reader = new FileReader();
          reader.onloadend = () => {
            setPreview(reader.result);
          };
          reader.readAsDataURL(v.upload);

          const data = new FormData();
          data.append(objkey, v.upload);
          setIsInvalid(null);
          onSubmitCallback(data);
        })
        .catch(err => {
          setIsInvalid(err.errors);
        });
    }
  }, [upload]);

  useEffect(() => {
    if (upload) {
      setUpload(null);
    }
  }, [img]);

  const imageSize = customPlaceholder ? 70 : isPhoneView ? 40 : 50;

  return (
    <div className={`d-flex align-items-center ${className || ''}`}>
      <div>
        <div className="text-primary font-weight-bold position-relative overflow-hidden">
          {
            <>
              <Form.Control
                placeholder="Enter label name"
                className="position-absolute c-pointer"
                style={{ opacity: 0, height: customPlaceholder ? '70px' : 'auto', zIndex: 9 }}
                size="sm"
                type="file"
                onChange={(e: React.ChangeEvent<{ files: File[] }>) => setUpload(e.currentTarget.files[0])}
                isInvalid={isInvalid}
                autoComplete="off"
                name="upload"
                accept="image/*"
              />
              {img || preview ? (
                <div className="position-relative">
                  <Image
                    height={imageSize}
                    width={imageSize}
                    src={preview || img || placeholder}
                    className={`border bg-light ${customPlaceholder ? 'rounded-circle' : 'rounded'}`}
                  />
                  <span
                    className="d-inline-block position-absolute p-1 bg-white rounded-circle text-muted"
                    style={{
                      bottom: 0,
                      right: 0,
                      lineHeight: 0.5,
                    }}
                  >
                    <IoIosCamera size={18} />
                  </span>
                </div>
              ) : (
                customPlaceholder
              )}
              <Form.Control.Feedback type="invalid" className="small">
                {isInvalid}
              </Form.Control.Feedback>
            </>
          }
        </div>
        {customPlaceholder ? null : (
          <i className="small text-muted">Note: Image of type jpeg/png and file size less than 2 mB</i>
        )}
      </div>
    </div>
  );
};

export default UploadImg;
